import keyMirror from 'keymirror'
import serverProps from './utils/serverProps'
import _ from 'lodash'

import {
  FUNDING_SOURCE_CLIENT_TAGS,
  PAYMENT_TYPE,
} from '../../../lib/constants'

const CDN_HOST_NAME = _.get(serverProps, 'cdnHostName')

export const TRANSFER_BASE_URL = '/myaccount/transfer'
export const SUMMARY_PAGE_URL = '/myaccount/summary'

export const QUALTRICS_APP_NAME = 'p2p-sendmoney-success-link'
export const PICTOGRAMS = {
  PAYCOMPLETEBLUE: `${CDN_HOST_NAME}/paypal-ui/pictograms/blue/svg/pay-complete-blue.svg`,
}

export const DC_SEND_MONEY = {
  SUCCESS: `${CDN_HOST_NAME}/p2p/dc/success-checkmark-outline.svg`,
  AMEX: `${CDN_HOST_NAME}/p2p/dc/amex_safekey_logo.png`,
}

export const PAYMENT_TYPE_FLOW_MAP = {
  [PAYMENT_TYPE.PURCHASE]: 'buy',
  [PAYMENT_TYPE.PERSONAL]: 'send',
}

export const GIFT_OPTIONS = {
  ecard: 'ecard',
  ppdg: 'ppdg',
}

export const GIFT_THEMES = [
  'hanukkah',
  'christmas',
  'chinese-new-year',
  'paypal',
  'love',
  'celebration',
]

export const MAX_RECIPIENTS = 20
export const MAX_AMOUNT_LENGTH = 10
export const MGM_AMOUNT = 0.01
export const ROOT = '/myaccount/'
export const ERROR_COMPLIANCE_DENIED = 'errorComplianceDecisionDeny'
export const UNAVAILABLE_FEATURE = 'featureUnavailable'
export const CLIENT_MISSING_REQUIRED_NOTE_FIELD = 'missingRequiredNoteField'

export const SEND_MONEY_ERRORS = {
  COUNTRY_DISALLOWED: 'errorPPShopsCountryDisallowed',
  SHOP_ITEM_UNAVAILABLE: 'errorPPShopsItemsUnavailable',
  INSTRUMENT_DEBIT_FAILED: 'errorPaymentFailedForFundingSource',
}
export const ERROR_PAYMENT_FAILURE = 'errorPaymentRelease'

// These errors will show a generic alert via "setShowGenericTopOfPageAlert".
export const SEND_MONEY_ERROR_ALERTS = [
  SEND_MONEY_ERRORS.COUNTRY_DISALLOWED,
  SEND_MONEY_ERRORS.SHOP_ITEM_UNAVAILABLE,
]

// These tags will be used to show an inline error beneath the funding option.
// NOTE: these will be rendered in the order that they appear in this array.
export const FUNDING_SOURCE_ERROR_ALERTS = [
  FUNDING_SOURCE_CLIENT_TAGS.INSTRUMENT_DECLINED,
]

export const defaultCardSpec = {
  type: 'card',
  fields: {
    ccNumber: {
      required: true,
      pattern: '4[0-9]{12}(?:[0-9]{3})?',
      maxlength: 19,
    },
    expirationDate: {
      required: true,
      pattern: '(0[1-9]|1[012])[/]((20)[0-9]{2}|[0-9]{2})',
      maxlength: 7,
    },
    csc: {
      required: true,
      pattern: '[0-9]*',
      maxlength: 3,
      minlength: 3,
    },
  },
}

export const CURRENCY_CONVERSION_OPTIONS = keyMirror({
  INTERNAL: null,
  EXTERNAL: null,
})

export const GOVT_ID_TYPES = {
  SSN: 'ssn',
  ITIN: 'itin',
  ALIEN: 'alienId',
  PASSPORT: 'passport',
}

export const WALLET = {
  NEW_BANK: 'newBank',
  NEW_FI: 'newFI',
  NEW_CARD: 'newCard',
  LINK_BANK_CARD: 'linkBankOrCard',
}

export const CIP = {
  CIP_REQUIRED: 'CIP_REQUIRED',
  MANUAL_REVIEW: 'MANUAL_REVIEW',
  CIP_ERROR: 'CIP_ERROR',
  VERIFIED: 'VERIFIED',
  FAILURE: 'FAILURE',
  SUCCESS: 'SUCCESS',
}

export const PAYMENT_TOKEN_TYPES = keyMirror({
  AMEX_P2P_PASS: null,
})

export const PAYMENT_CARDS = keyMirror({
  VISA: null,
  MASTERCARD: null,
  MASTER_CARD: null,
  MAESTRO: null,
  AMEX: null,
  DISCOVER: null,
  JCB: null,
  CHINA_UNION_PAY: null,
})

// this is a reverse mapping of the enumeration in consumerweb-card:
// https://github.paypal.com/ConsumerWeb-Modules/consumerweb-card/blob/master/models/constants/cardProperties.js#L98
// it is needed because the cardSpec prop in redux store has type as lowercase
// e.g. 'visa', whereas the cardSpecs.cardTypes object (which contains all the
// cardSpecs for a given user/locale) has each cardSpec as an uppercase property
// e.g. 'VISA', and sometimes we have to map between these two. The cardSpecs
// object is returned from consumerweb-card
//
// TODO: maybe clean up/streamline how we/consumerweb-card handle this data
export const cardSpecTypesToCardTypeConstants = {
  visa: 'VISA',
  master_card: 'MCARD',
  amex: 'AMEX',
  discover: 'DISC',
  maestro: 'MAESTRO',
  cb_nationale: 'CBANC',
  cetelem: 'AURORE',
  cofinoga: 'COP',
  cofidis: '4ETOILES',
  elo: 'ELO',
  hipercard: 'HIPERCARD',
  hiper: 'HIPER',
  jcb: 'JCB',
  china_union_pay: 'CUP',
  switch: 'SWITCH',
}

// guest flow uses consumerweb-payment-signup which uses checkout code, and the
// card types are slightly different: https://github.paypal.com/ConsumerWeb-Modules/consumerweb-payment-signup
export const cardSpecTypesToGuestAddCardTypes = {
  visa: 'VISA',
  master_card: 'MASTER_CARD',
  amex: 'AMEX',
  discover: 'DISCOVER',
  maestro: 'MAESTRO',
  cb_nationale: 'CB_NATIONALE',
  cetelem: 'CETELEM',
  cofinoga: 'COFINOGA',
  cofidis: 'COFIDIS',
  elo: 'ELO',
  hipercard: 'HIPERCARD',
  hiper: 'HIPER',
  jcb: 'JCB',
  switch: 'SWITCH',
}

export const CREDIT_PROVIDERS = keyMirror({
  PLUS_CARD: null,
  VIRTUAL_LINE: null,
  EBAY_CARD: null,
  PAYPAL_CREDIT: null,
})

export const BANK_ACCOUNT_TYPES = keyMirror({
  CHECKING: null,
  SAVINGS: null,
})

export const FLOW_TYPES = {
  SEND: 'send',
  BUY: 'buy',
  PAY_REQUEST: 'payRequest',
  CROWD_FUNDING: 'crowdfunding',
  REQUEST: 'request',
  GIFT: 'gift',
  PPSHOPS: 'shops',
}

export const PROFILE_SOURCE = {
  SEND: 'pp_me_profile_send',
  REQUEST: 'pp_me_profile_request',
}

export const HAWK_SOURCE = {
  HAWK_WEB: 'hawk_web',
  HAWK_MWEB: 'hawk_mweb',
}

export const MAX_AMOUNT_DIGIT_LENGTH = 10

export const AUTOCOMPLETE_ITEM_SIZE = {
  ITEM: 80,
  ITEM_SM: 64,
  TITLE: 50,
  TITLE_SM: 34,
}

export const AUTOCOMPLETE_ITEM_TYPE = {
  MESSAGE: 'MESSAGE',
  TURNON: 'TURNON',
  SPINNER: 'SPINNER',
  PHONE_OR_EMAIL: 'PHONE_OR_EMAIL',
  TITLE: 'TITLE',
}

export const CONTACT_SELECTED_GROUP = {
  TOP_CONTACTS: 'top_contacts',
  YOUR_CONTACTS: 'your_contacts',
  PEOPLE_ON_PAYPAL: 'people_on_paypal',
  PHONE_OR_EMAIL: 'phone_or_email_only',
}

export const CONTACT_SELECTION_SEARCH_TYPE = {
  EMAIL: 'email',
  PHONE: 'phone',
  PPME: 'ppme',
  NAME: 'name',
}

export const CONSENT_TYPE = {
  SIGNUP_FOR_NETWORK_ID: 'SIGNUP_FOR_NETWORK_ID',
  PROVIDE_ADDITIONAL_NETWORK_ID_CONSENT:
    'PROVIDE_ADDITIONAL_NETWORK_ID_CONSENT',
  UPGRADE_NETWORK_ID: 'UPGRADE_NETWORK_ID',
}

export const SECONDARY_USER_PRIVILEGES = {
  SEND_MONEY: 'send_money',
  REQUEST_MONEY: 'request_money',
  MASS_PAY: 'mass_pay',
  SEND_MONEY_REQUEST: 'send_money_request',
}

export const MGM = {
  US_COUNTRY_CODE: 'US',
  PROMO_SEND_AMOUNT: '5',
  PROMO_RECEIVE_AMOUNT: '10',
  PROMO_EXPIRY_DATE: '12/31/20',
  MAX_PROMO_AMOUNT: '50',
  MAX_PROMO_AMOUNT_EXTENDED: '100',
  MGM_IMAGE_SRC: `${CDN_HOST_NAME}/referralsnodeweb/mgm-landing-web-v2.png`,
  MGM_DELAYED_PAYOUT_DURATION_IN_DAYS: 10,
}

export const MGM_TRAFFIC_SOURCES = {
  CONTACTS: 'web_contacts',
  SEND: 'web_send_money_right_nav',
  REQUEST: 'web_request_money_right_nav',
  MORE: 'web_more',
}

export const DONATION_VALIDATION_TYPES = {
  AMOUNT_ABOVE_MAXIMUM: 'AMOUNT_ABOVE_MAXIMUM',
  AMOUNT_BELOW_MINIMUM: 'AMOUNT_BELOVE_MINIMUM',
  AMOUNT_VALID: 'AMOUNT_VALID',
}

export const DONATE_MAX_CHAR_LIMIT = 250
export const REQUEST_MAX_CHAR_LIMIT = 1500

export const INSTRUMENTATION_TYPES = keyMirror({
  ERROR: null,
  ACTION: null,
  ACTION_WITH_DATA: null,
  PAGE_CHANGE: null,
  IMPRESSION: null,
})

export const OPEN_BANKING_FLOWS = keyMirror({
  ADD_BANK: null,
  CONSENT_CONTINGENCY: null,
  REFRESH_CONTINGENCY: null,
})

export const RELATIVE_DATES = keyMirror({
  TODAY: null,
  YESTERDAY: null,
})

export const STORY_ALERTS = keyMirror({
  BLOCK_SUCCESS: null,
  BLOCK_TRY_AGAIN: null,
  BLOCK_ERROR: null,
})

export const REQUIRED_FLOW_ATTRIBUTES = keyMirror({
  NOTE_TO_RECEIVER: null,
})

export const ActionTypes = {
  ERROR: keyMirror({
    SHOW_ERROR_OVERPANEL: null,
    SHOW_ERROR_PAGE: null,
    THREE_DS2_WARNING_OVERPANEL: null,
    ENCRYPTION_FAILED: null,
  }),

  USER: keyMirror({
    CHANGE_COUNTRY: null,
    CHANGE_PRIMARY_CURRENCY_CODE: null,
    CHANGE_EMAIL: null,
    CHANGE_FIRST_NAME: null,
    CHANGE_LAST_NAME: null,
    CHANGE_PHONE_NUMBER: null,
    CHANGE_BILLING_ADDRESS_ENTRY_FIELD: null,
    CHANGE_SHIPPING_ADDRESS_ENTRY_FIELD: null,
    CHANGE_TRAVEL_RULE_ADDRESS_ENTRY_FIELD: null,
    SET_PASSWORD: null,
    CREATE_TEMP_ACCOUNT: null,
    CREATED_TEMP_ACCOUNT: null,
    CREATE_FULL_ACCOUNT: null,
    CREATED_FULL_ACCOUNT: null,
    UPGRADE_ACCOUNT: null,
    UPGRADED_ACCOUNT: null,
    CHANGE_GOVT_ID: null,
    CHANGE_SELECTED_GOVERNMENT_ID_TYPE: null,
    CHANGE_BIRTH_DAY: null,
    CHANGE_BIRTH_MONTH: null,
    CHANGE_BIRTH_YEAR: null,
    TOGGLE_SENDER_HELPER_INFORMATION: null,
    CHANGE_SELECTED_SHIPPING_ADDRESS: null,
    NO_SELECTED_SHIPPING_ADDRESS: null,
    CHANGE_SHIPPING_ADDRESS_CHECKBOX: null,
    CHANGE_SELECTED_BILLING_ADDRESS: null,
    RECEIVED_UPGRADE_ELIGIBILITY: null,
    RECEIVED_GRIFFIN_METADATA: null,
    PROPOSE_PHOTO: null,
    SET_IS_LOADING: null,
    TRIGGER_SENDER_PHONE_CONFIRMATION: null,
    DISABLE_PERSONAL_PAYMENT: null,
    DISABLE_PURCHASE_PAYMENT: null,
    REQUIRE_INSTRUMENT_ADDITION: null,
  }),

  TRANSACTION: keyMirror({
    CHANGE_HIGHLIGHTED_PAYMENT_TYPE: null,
    CHANGE_RECOMMENDED_PAYMENT_TYPE: null,
    CHANGE_IS_HARD_DEFAULT: null,
    SAVE_SUCCESS_ID: null,
    SET_OPEN_BANKING_ERROR: null,
    CHANGE_IS_PURCHASE: null,
    CHANGE_AMOUNT: null,
    FETCH_SPECS: null,
    RECEIVED_SPECS: null,
    CHANGE_CARD_NUMBER: null,
    CHANGE_EXPIRY: null,
    CHANGE_SECURITY_CODE: null,
    CHANGE_CARD_TYPE: null,
    GUEST_FETCH_FUNDING_OPTIONS: null,
    TRACK_RECEIVING_LIMIT: null,
    FETCH_FUNDING_OPTIONS: null,
    FETCH_EXTERNAL_FUNDING_OPTIONS: null,
    RECEIVED_FUNDING_OPTIONS: null,
    REFETCHED_FUNDING_OPTIONS: null,
    CLEAR_FUNDING_OPTIONS: null,
    SELECT_PAYMENT_TYPE: null,
    PAY_REQUEST: null,
    PAID_REQUEST: null,
    REQUEST_MONEY: null,
    REQUESTED_MONEY: null,
    CANCELLED_TRANSACTION: null,
    UPDATE_MONEY_PAGE: null,
    UPDATE_SUGGESTED_DESIGN: null,
    LEAVING_SUCCESS_PAGE: null,
    POST_TRAVEL_RULE_DATA: null,
    POSTED_TRAVEL_RULE_DATA: null,
    INTENDS_TO_CREATE_FULL_ACCOUNT: null,
    INTENDS_TO_ADD_SHIPPING_ADDRESS: null,
    CHANGE_SELECTED_FUNDING_OPTION: null,
    USE_CHOICE: null,
    ADD_CARD: null,
    ADDED_CARD: null,
    SUCCESS_FB_ADD_CARD: null,
    CHANGE_SELECTED_INSTALLMENT_PLAN: null,
    SEND_MONEY: null,
    SET_DUALCONTROL_CONTEXT: null,
    SENT_MONEY: null,
    SHOW_UNILATERAL: null,
    NO_FUNDING_SOURCE: null,
    CHANGE_CVV_CODE: null,
    REQUEST_CVV: null,
    RESOLVE_CVV: null,
    RESOLVE_3DS: null,
    POST_PREFERRED_FUNDING_SOURCE: null,
    POSTED_PREFERRED_FUNDING_SOURCE: null,
    UPDATE_PREFERRED_IN_CLIENT_DATA: null,
    HIT_SENDING_LIMIT: null,
    SUBMITTING_RECIPIENT: null,
    HAS_REDIRECT_FROM_SEND: null,
    CHANGE_PURCHASE_LOCATION: null,
    CHANGE_ITEM_CATEGORY: null,
    UPDATE_GUEST_FF_ELIGIBILITY: null,
    SET_SUGGESTED_TRANSACTION_TYPE: null,
    SET_SERVICE_RETURNED_PAYMENT_TYPE: null,
    STORE_ALL_DDC: null,
    START_DDC_CALL: null,
    END_DDC_CALL: null,
    DDC_RESOLVED: null,
    THREE_DS2_SECURE_AUTH_REQUIRED: null,
    RESOLVED_3DS2_AUTH_CONTINGENCY: null,
    FAILED_3DS2_AUTH_CONTINGENCY: null,
    RESOLVE_RTB_CONTINGENCY: null,
    RTB_ERROR: null,
    RTB_INVALID_CREDENTIALS: null,
    RESOLVED_RTB_CONTINGENCY: null,
    UNAVAILABLE_FI: null,
    UPDATE_TOKEN: null,
    MGM_INIT: null,
    MGM_CUSTOM_FIELDS: null,
    RECIPIENT_CURRENCY_RESTRICTED: null,
    SET_FRAUDNET_CLIENT_METADATA: null,
    REFETCH_FUNDING_OPTIONS_3DS: null,
    GUEST_REVAMP_UPDATE_CVV: null,
    GUEST_REVAMP_UPDATE_ADDRESS_ID: null,
    UPDATE_PAYMENT_TYPE_SELECTION_MESSAGE: null,
    UPDATE_PAYMENT_TYPE_TOGGLE_PERSONAL_MESSAGE: null,
    UPDATE_PAYMENT_TYPE_TOGGLE_PURCHASE_MESSAGE: null,
    UPDATE_PAYMENT_TYPE_TRANSITIONS: null,
    PURCHASE_CONSENT_IS_CHECKED: null,
    NOTE_FIELD_IS_RESTRICTED: null,
    RESOLVE_PURCHASE_CONSENT: null,
    REQUEST_PURCHASE_CONSENT: null,
    FUNNEL_STARTED: null,
    CONTACT_ENGAGED: null,
    HAS_SEEN_NOTE: null,
    SET_SUGGESTED_INSTRUMENT_CONTEXT: null,
    REQUEST_USER_AGREEMENT_CONSENT: null,
    RESOLVE_USER_AGREEMENT_CONSENT: null,
  }),

  EXTERNAL: keyMirror({
    RECEIVED_EXTERNAL_PARAMS: null,
    RECEIVED_EXTERNAL_INTEGRATION_PARAMS: null,
    RECEIVED_OPTIONS: null,
    RECEIVED_DATA: null,
    TOGGLE_SHARE_ADDRESS: null,
    SHOW_AMOUNT_IS_ZERO_ERROR: null,
    HIDE_AMOUNT_IS_ZERO_ERROR: null,
    SHOW_AMOUNT_DONATION_ERROR: null,
    HIDE_AMOUNT_DONATION_ERROR: null,
    SHOW_NOTE_MAX_CHAR_LIMIT_ERROR: null,
    HIDE_NOTE_MAX_CHAR_LIMIT_ERROR: null,
    CHANGE_GIFTAID: null,
    TOGGLE_PHONE_CONFIRMATION: null,
    REDIRECT_TO_HOMEINFO: null,
    CHANGE_DONATION_ANONYMOUS_STATUS: null,
  }),

  VISAPLUS: keyMirror({
    TOGGLE_ACCOUNT_ACTIVATION_STATUS: null,
    CREATE_PAYNAME: null,
    EVALUATE_PROFILE: null,
    VERIFY_RECEIVER_PAYNAME: null,
  }),

  XSELL: keyMirror({
    DONE_XSELL_CONTENT: null,
  }),

  RECIPIENT: keyMirror({
    SELECT_UNILATERAL_CONTACT: null,
    SELECT_CONTACT: null,
    SELECT_PEER: null,
    CHANGE_RECIPIENT: null,
    CHANGE_TYPED_RECIPIENT: null,
    CHANGE_DISPLAY_NAME: null,
    RECEIVED_RECIPIENT_DETALS: null,
    REQUEST_UNILATERAL_RECIPIENT_CHOICES: null,
    RECEIVED_UNILATERAL_RECIPIENT_CHOICES: null,
    MGM_RECIPIENT_ERROR: null,
  }),

  RECIPIENTS: keyMirror({
    REMOVE_RECIPIENT: null,
    CHANGE_CURRENT_RECIPIENT: null,
    CHANGE_CURRENT_RECIPIENT_DISPLAY_NAME: null,
    CHANGE_RECIPIENT_AVAILABILITY: null,
    CHANGE_SELECTED_RECIPIENT_INDEX: null,
    SELECT_CONTACT: null,
    SELECT_PEER: null,
    UPDATE_RECIPIENT_LIST: null,
    RESET_RECIPIENTS: null,
  }),

  NOTE: keyMirror({
    CHANGE_NOTE: null,
    REQUIRED_FIELD_ERROR: null,
  }),

  GIFT_OPTIONS: keyMirror({
    CHANGE_OPTION: null,
  }),

  THEME: keyMirror({
    CHANGE_THEME_ID: null,
  }),

  AMOUNT: keyMirror({
    RECEIVED_CURRENCY_LIST: null,
    RECEIVED_CURRENCY_FORMAT: null,
    CHANGE_AMOUNT: null,
    CHANGE_CURRENCY: null,
    CHANGE_REQUEST_CURRENCY: null,
    UPDATE_RECIPIENT_CURRENCIES: null,
    ADD_RECEIVER_FEE: null,
  }),

  PPME: keyMirror({
    CREATE_PROFILE: null,
    CREATED_PROFILE: null,
    UPDATE_PROFILE: null,
    UPDATED_PROFILE: null,
  }),

  CONTACTS: keyMirror({
    LOADING_BLOCKED_CONTACTS: null,
    RECEIVED_CONTACTS: null,
    RECEIVED_BLOCKED_CONTACTS: null,
    DELETE_CONTACT: null,
    DELETED_CONTACT: null,
    UPDATE_CONTACT_BLOCK: null,
    UPDATED_CONTACT_BLOCK: null,
    RECEIVED_CONTACT_DATA: null,
    RECEIVED_SEARCHED_CONTACTS: null,
    FAVORITE_CONTACT: null,
    FAVORITED_CONTACT: null,
  }),

  PEERS: keyMirror({
    RECEIVED_SEARCHED_PEERS: null,
    RECEIVED_LOADED_PEERS: null,
    RECEIVED_LOADED_BUSINESSES: null,
    RETRIEVED_PEERS: null,
    SEARCHING_PEERS: null,
    LOADING_PEERS: null,
    LOADING_BUSINESSES: null,
    SEARCH_ERROR: null,
  }),

  CROSS_BORDER: keyMirror({
    SET_INITIAL_STATE: null,
    RECEIVED_FEE: null,
    CHANGE_DESTINATION_AMOUNT: null,
    CHANGE_DESTINATION_CURRENCY_CODE: null,
    CHANGE_ORIGIN_AMOUNT: null,
    CHANGE_ORIGIN_CURRENCY_CODE: null,
    REQUEST_ORIGIN_AMOUNT: null,
    RECEIVED_ORIGIN_AMOUNT: null,
    REQUEST_DESTINATION_AMOUNT: null,
    RECEIVED_DESTINATION_AMOUNT: null,
    REQUEST_EXCHANGE_RATE: null,
    RECEIVED_EXCHANGE_RATE: null,
    FETCH_CONVERSION_FAILED: null,
    REQUEST_RECIPIENT_DETAILS: null,
    RECEIVED_RECIPIENT_DETAILS: null,
    RECEIVED_FX_QUOTE_ID: null,
    ORIGIN_AMOUNT_LOADED: null,
    CHANGE_DESTINATION_COUNTRY: null,
  }),

  OVERPANELS: keyMirror({
    TOGGLE_CASH_ADVANCE_FEES_ABOUT_OVERPANEL: null,
    TOGGLE_CASH_ADVANCE_FEES_DISCLOSURE_OVERPANEL: null,
    UPDATE_HAS_SHOWN_CASH_ADVANCE_FEES_OVERPANEL: null,
    TOGGLE_SEARCH_CONSENT_OVERPANEL: null,
    TOGGLE_PHOTO_UPLOAD_OVERPANEL: null,
    UPDATE_UPLOADED_PHOTO_URL: null,
    UPDATE_HAS_SHOWN_SEARCH_CONSENT_OVERPANEL: null,
    TOGGLE_PAYMENT_TYPE_CHOICE_OVERPANEL: null,
    TOGGLE_UNILATERAL_RECIPIENT_CHOICE_OVERPANEL: null,
    TOGGLE_CONTACT_DETAILS_OVERPANEL: null,
    TOGGLE_ADD_SHIPPING_ADDRESS_OVERPANEL: null,
    TOGGLE_ADD_BILLING_ADDRESS_OVERPANEL: null,
    TOGGLE_ADD_CARD_OVERPANEL: null,
    TOGGLE_CURRENCY_CONVERSION_OVERPANEL: null,
    TOGGLE_3D_SECURE_OVERPANEL: null,
    TOGGLE_SENDING_LIMIT_OVERPANEL: null,
    TOGGLE_FUNDING_OPTIONS_OVERPANEL: null,
    TOGGLE_ERROR_OVERPANEL: null,
    ADD_OPEN_OVERPANEL: null,
    SUBTRACT_CLOSED_OVERPANEL: null,
    TOGGLE_THREEDS2_CHALLENGE_OVERPANEL: null,
    TOGGLE_THREEDS2_SPINNER_OVERPANEL: null,
    TOGGLE_RISK_HOLDS_OVERPANEL: null,
    CONFIRMED_RISK_HOLDS: null,
    TOGGLE_OPEN_BANKING_OVERPANEL: null,
    TOGGLE_CONFIRM_IDENTITY_OVERPANEL: null,
    TOGGLE_BLOCK_CONTACT_OVERPANEL: null,
    TOGGLE_BLOCKED_CONTACTS_OVERPANEL: null,
    TOGGLE_CONTACT_BLOCKED_OVERPANEL: null,
    TOGGLE_CONTACT_UNBLOCKED_OVERPANEL: null,
    TOGGLE_CHAT_BLOCK_CONTACT_OVERPANEL: null,
    PURCHASE_TO_PERSONAL_TRANSACTION_OVERPANEL: null,
    STOP_LOADING: null,
    TOGGLE_UNILATERAL_EMAIL_CONFIRMATION_OVERPANEL: null,
  }),

  ADDRESS: keyMirror({
    ADD_ADDRESS: null,
    ADDED_SHIPPING_ADDRESS: null,
    ADDED_BILLING_ADDRESS: null,
    NO_BILLING_ADDRESS: null,
  }),

  BILL_SPLITTING: keyMirror({
    CHANGE_REQUESTER_AMOUNT: null,
    CHANGE_RECIPIENT_LIST: null,
    UPDATE_FROZEN_INDICATORS: null,
    RESTORE_SPLIT_STATE: null,
    CHANGE_TAB: null,
    CHANGE_INCLUDE_REQUESTER: null,
  }),

  UNILATERAL: keyMirror({
    PORTABLE_ONBOARDING_IS_LOADING: null,
    PORTABLE_ONBOARDING_NOT_LOADING: null,
    CHANGE_UNILATERAL_NAME: null,
    CHANGE_UNILATERAL_COUNTRY_CODE: null,
    CLAIM_SUCCESS: null,
    CLAIM_ERROR: null,
    CLAIM_PROCESS_LINKING: null,
    RESEND_MAIL_STATUS: null,
  }),

  STORY: keyMirror({
    SEND_MESSAGE: null,
    REMOVE_MESSAGE: null,
    MESSAGE_ERROR: null,
    CHAT_UPDATE_CONTACT_BLOCK: null,
    CHAT_UPDATED_CONTACT_BLOCK: null,
    RENDER_BLOCK_ALERT: null,
    DISMISS_BLOCK_ALERT: null,
  }),

  INSTRUMENTATION: keyMirror({
    // Generally you should attach click tracking meta data to other actions,
    // occassionally though no other action state change is happening on click.
    // In that case use GENERIC_CLICK_TRACKING.
    GENERIC_CLICK_TRACKING: null,

    PURCHASE_PROTECTION: null,
    CLEAR_PURCHASE_PROTECTION: null,
  }),

  ROUTER: {
    LOCATION_CHANGE: '@@router/LOCATION_CHANGE', // this is the string type constant for actions fired by redux router middleware
    REDIRECT: 'REDIRECT',
  },
}

export const OB_UPDATE_EVENT_TYPE = 'UPDATE_EXTERNAL_VALUES'

export const PAYPAL_LOGO = `${CDN_HOST_NAME}/paypal-ui/logos/svg/paypal-color.svg`

/**
 * SPF lifecycle events
 * - https://github.paypal.com/pages/ConsumerWeb-R/spf-web/develop/?path=/docs/developer-spf-web-sdk-lifecycle-events--page
 * - https://github.paypal.com/ConsumerWeb-R/spf-web/blob/develop/packages/spf-sample-app/src/usage.js#L29-L71
 */
export const SPF_LIFECYCLE_EVENTS = {
  CLICK: 'CLICK',
  ERROR: 'ERROR',
  NAVIGATE: 'NAVIGATE',
  CALLBACK: 'CALLBACK',
}
export const SPF_LIFECYCLE_EVENTS_VALUES = Object.values(SPF_LIFECYCLE_EVENTS)
